import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { ExpensesSettingsPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Upgrade from 'modules/core/components/Upgrade'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import ApprovalsBlock from 'modules/approvals/components/ApprovalsConfigurationBlock'
import { useApprovalFeatures } from 'modules/approvals/hooks/useFeatureLimits'
import UpgradePlanModal from 'modules/expenses/components/UpgradePlanModal'
import ExpensePolicy from 'modules/expenses/pages/settings/ExpensePolicy'
import { missingFeatureRequestReason } from 'modules/finance/lib/missingFeatureRequestReason'
import PageLoading from 'modules/layout/components/Page/Loading'
import { UpsellingKit } from 'modules/upselling_kit/components'

import AccountingSection from './Accounting'
import DiscoverMoreSection from './DiscoverMore'
import ExpensesGroupsSection from './ExpensesGroups'
import FormsSection from './Forms'
import LegalComplianceSection from './LegalCompliance'
import RateConfigurationSection from './RateConfigurations'

const Content = () => {
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false)
  const [showUpgradeModalFlow, setShowUpgradeModalFlow] = React.useState(false)
  const legalComplianceRef = React.useRef<HTMLDivElement>(null)

  const { canAddGroup } = useApprovalFeatures('expenses')
  const { company } = useCurrentSession()
  const { search } = useLocation()

  const { data, loading } = useQuery(ExpensesSettingsPageDocument, {
    variables: {
      ids: [
        Features.finance_ledger_accounts_core,
        Features.expenses_report_on_behalf,
        Features.expenses_expensable_groups,
        Features.expenses_per_diems,
        Features.expenses_form_customization,
      ],
      companyId: company.id,
    },
  })

  const featureAvailabilities = data?.companies.featureAvailabilitiesConnection.nodes ?? []
  const hasLedgerAccountFeature =
    featureAvailabilities.find((feature) => feature.id === Features.finance_ledger_accounts_core)
      ?.available ?? false
  const hasReportOnBehalfFeature =
    featureAvailabilities.find((feature) => feature.id === Features.expenses_report_on_behalf)
      ?.available ?? false
  const hasExpensesGroupsFeature =
    featureAvailabilities.find((feature) => feature.id === Features.expenses_expensable_groups)
      ?.available ?? false
  const hasPerDiemsFeature =
    featureAvailabilities.find((feature) => feature.id === Features.expenses_per_diems)
      ?.available ?? false
  const hasExpensesFormCustomizationFeature =
    featureAvailabilities.find((feature) => feature.id === Features.expenses_form_customization)
      ?.available ?? false
  const hasNewPerDiemFeature =
    data?.features?.featuresConnection?.nodes.find(
      (feature) => feature.name === 'dev_expenses_germany_per_diem'
    )?.enabled ?? false
  const hasExpensePolicyFeature =
    data?.features?.featuresConnection?.nodes.find(
      (feature) => feature.name === 'dev_expenses_policy_settings'
    )?.enabled ?? false
  const hasExpenseApprovalWorkflowsFeature =
    data?.features?.featuresConnection?.nodes.find(
      (feature) => feature.name === 'dev_expenses_approval_workflows'
    )?.enabled ?? false
  const isExpensesGroupsEnabled =
    data?.expenses.expensableGroupsSettingsConnection.nodes[0]?.enabled ?? false

  const canSeeLedgerAccounts = data?.finance?._policies?.accountingSeeAccounts?.can ?? false
  const canEditApprovers = data?.approvals?._policies.approvalStepApproversEdit?.can ?? false

  const signedDocumentsTerms = data?.expenses.signedDocumentsTerms ?? []

  useEffect(() => {
    if (loading) return

    if (search.includes('legal-compliance') && legalComplianceRef.current) {
      legalComplianceRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [search, loading])

  if (!data) {
    return <PageLoading />
  }

  if (hasExpensePolicyFeature) {
    return (
      <ExpensePolicy
        canEditApprovers={canEditApprovers}
        hasPerDiemsFeature={hasPerDiemsFeature}
        hasNewPerDiemFeature={hasNewPerDiemFeature}
        hasExpensesGroupsFeature={hasExpensesGroupsFeature}
        isExpensesGroupsEnabled={isExpensesGroupsEnabled}
        hasExpensesFormCustomizationFeature={hasExpensesFormCustomizationFeature}
        hasLedgerAccountFeature={hasLedgerAccountFeature}
        canSeeLedgerAccounts={canSeeLedgerAccounts}
        signedDocumentsTerms={signedDocumentsTerms}
        hasReportOnBehalfFeature={hasReportOnBehalfFeature}
      />
    )
  }

  return (
    <>
      <Box width="full" maxWidth="full" gap="s64">
        {canEditApprovers && (
          <ApprovalsBlock
            category="expenses"
            title={i18n.t('expenses.settings.approvals.title')}
            description={i18n.t('expenses.settings.approvals.description')}
            noPadding
            multigroup
            onAddGroup={
              !canAddGroup()
                ? () => {
                    setShowUpgradeModal(true)
                  }
                : undefined
            }
            icon={'document-approved'}
            hideAddGroupButton={!canAddGroup()}
            addGroupButtonFallback={
              <Upgrade.Small
                title={i18n.t('settings.approvals.upgrade')}
                action={{
                  type: 'know_more',
                  onClick: () => setShowUpgradeModalFlow(true),
                }}
              />
            }
          />
        )}
        <FormsSection
          hasExpensesFormCustomizationFeature={hasExpensesFormCustomizationFeature}
          hasPerDiemsFeature={hasPerDiemsFeature}
        />

        <RateConfigurationSection
          hasPerDiemsFeature={hasPerDiemsFeature}
          hasNewPerDiemFeature={hasNewPerDiemFeature}
        />

        <ExpensesGroupsSection
          hasExpensesGroupsFeature={hasExpensesGroupsFeature}
          isExpensesGroupsEnabled={isExpensesGroupsEnabled}
        />
        <Box ref={legalComplianceRef}>
          <LegalComplianceSection signedDocumentsTerms={signedDocumentsTerms} />
        </Box>
        <AccountingSection
          hasFeature={hasLedgerAccountFeature}
          canGoToSettings={canSeeLedgerAccounts}
        />
        <DiscoverMoreSection hasReportOnBehalfFeature={hasReportOnBehalfFeature} />
        {/* only used in approvals section when chargebee features/paywalls are not enabled yet*/}
        {showUpgradeModal && (
          <UpgradePlanModal
            origin="createPolicy"
            onClose={() => {
              setShowUpgradeModal(false)
            }}
          />
        )}
        {showUpgradeModalFlow && (
          <UpsellingKit.ProductModal
            isOpen={showUpgradeModalFlow}
            onClose={() => setShowUpgradeModalFlow(false)}
            requestSource="Expenses settings page"
            featureRequested={Features.expenses_approval_policies}
            moduleRequested={AvailableModules.expenses}
            requestReason={missingFeatureRequestReason(company)}
            pageSource="expenses_settings"
          />
        )}
      </Box>
    </>
  )
}

export default observer(Content)
